import Image from "../../../../../components/elements/Image";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Input from "../../../../../components/form/Input";
import Checkbox from "../../../../../components/form/Checkbox";
import Button from "../../../../../components/form/Button";
import EventBG from "../../../../../assets/images/event-list.webp";
import useForm from "../../../../../hooks/useForm";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { login } from "../../../../../redux/slice/authSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { t } from "i18next";
import GoogleLoginButton from "../../../../../components/elements/GoogleLoginButton";

const validation = {
  email: { required: true, message: "Please enter your email!" },
  password: { required: true, message: "Please enter your password!" },
};

// Utility functions for saving/retrieving credentials
const saveCredentials = (email, password, remember) => {
  localStorage.setItem("savedEmail", email);
  localStorage.setItem("rememberMe", remember);
  localStorage.setItem("savedPassword", btoa(password)); // Base64 encoding (not very secure)
  localStorage.setItem("rememberMe", remember);
};

const getSavedCredentials = () => {
  if (localStorage.getItem("rememberMe") === "true") {
    return {
      email: localStorage.getItem("savedEmail") || "",
      password: localStorage.getItem("savedPassword")
        ? atob(localStorage.getItem("savedPassword"))
        : "",
      remember: true,
    };
  }
  return { email: "", password: "", remember: false };
};

const clearSavedCredentials = () => {
  localStorage.removeItem("savedEmail");
  localStorage.removeItem("savedPassword");
  localStorage.removeItem("rememberMe");
};

const Login = () => {
  const dispatch = useDispatch();
  const { lang } = useSelector((state) => state?.langProvider);
  const location = useLocation();
  const params = new URLSearchParams(location?.search);
  const { callback } = Object.fromEntries(params.entries());
  const navigate = useNavigate();

  const savedCredentials = getSavedCredentials();

  const {
    values,
    handleChange,
    setFieldsValue,
    handleSubmit,
    errors,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: savedCredentials,
  });

  const { loginPending } = useSelector((state) => state?.authProvider);

  useEffect(() => {
    const saved = getSavedCredentials();
    if (saved.email || saved.password) {
      setFieldsValue(saved);
    }
  }, []);

  const { mutate: loginMutate } = useMutation({
    mutationFn: async (val) => {
      const loginData = {
        ...val,
        type: "user",
        redirect: callback ? callback : `/${lang}/profile`,
        location,
        navigate,
      };

      const res = await dispatch(login(loginData));
      return res;
    },
    onSuccess: (res) => {
      if (values?.remember && res?.payload?.status === 200) {
        saveCredentials(values.email, values.password, values.remember);
      } else {
        clearSavedCredentials();
      }
    },
    onError: (error) => {
      toast.error(error.message || "Login failed!");
    },
  });

  return (
    <div className="flex items-center justify-center bg-gradient-to-br from-[#F9F6FF] to-[#F4F1FD] p-6  relative">
      <div className="p-2 border-2 border-white rounded-4xl overflow-hidden max-w-6xl w-full">
        <div className="grid grid-cols-12  bg-white rounded-4xl overflow-hidden">
          <div className="col-span-6 py-14 px-20 flex flex-col justify-center">
            <Link
              to="/"
              className="w-48 overflow-hidden aspect-[7/1] block mb-5"
            >
              <Image
                src={require("../../../../../assets/images/logo.png")}
                classes={"object-contain"}
              />
            </Link>
            <h1 className="text-2xl font-bold text-deep-purple mb-1">
              {t("Welcome! What's your email?")}
            </h1>
            <div className="text-slate-500 mb-5">
              {t("Fill the below form to login")}
            </div>
            <form
              className="space-y-6"
              onSubmit={(event) => handleSubmit(event, loginMutate)}
            >
              <div className="relative">
                <Input
                  label={t("email")}
                  labelClasses="!text-base capitalize"
                  inputType="email"
                  inputClasses={"h-14 focus:!border-primary"}
                  inputName={"email"}
                  value={values?.email}
                  onChange={handleChange}
                  {...formValidate(errors, "email")}
                />
              </div>
              <div className="relative">
                <Input
                  label={t("password")}
                  labelClasses="!text-base capitalize"
                  inputType="password"
                  inputClasses={"h-14 focus:!border-primary"}
                  inputName={"password"}
                  value={values?.password}
                  onChange={handleChange}
                  {...formValidate(errors, "password")}
                />
              </div>
              <div className="flex justify-between items-center text-sm">
                <div className="relative">
                  <Checkbox
                    checkboxLabel={t("remember me")}
                    checkboxLableclassName={
                      "text-sm capitalize text-text-disabled"
                    }
                    checkboxName={"remember"}
                    value={values?.remember}
                    checked={values?.remember}
                    onChange={handleChange}
                  />
                </div>
                <div className="relative">
                  <Link
                    to="/forgot-password"
                    className="text-deep-purple hover:underline"
                  >
                    {t("Forgot Password?")}
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  buttonType="submit"
                  buttonIconPosition={"left"}
                  buttonIcon={"fa-regular fa-arrow-right"}
                  buttonLabel={t("login")}
                  buttonLabelClasses="capitalize"
                  {...(loginPending && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <GoogleLoginButton />
              </div>
            </form>
            <div className="mt-5 text-slate-500 text-sm">
              {t("Don’t have an account?")}{" "}
              <Link
                to={`/register${callback ? "?callback=" + callback : ""}`}
                className="text-deep-purple hover:underline font-medium"
              >
                {t("Sign up")}
              </Link>
            </div>
          </div>
          <div className="col-span-6 pt-5 pr-5 pb-5 pl-0">
            <div className="space-y-5 rounded-2xl overflow-hidden bg-gradient-to-br from-[#F9F6FF] to-[#F4F1FD] py-14 pl-10 flex flex-col w-full">
              <div className="capitalize text-slate-500 text-xs bg-white border border-text-disabled rounded-md p-1 max-w-60 text-center flex items-center justify-center">
                {t("dancebrite system pressure control")}
              </div>
              <div className="text-slate-500 font-medium text-base max-w-96 w-full">
                {t("Manage your concreting and construction operation")}{" "}
                <span className="text-deep-purple0">
                  {t("more professionally")}
                </span>
              </div>
              <div className="w-full aspect-[5/4] overflow-hidden">
                <Image src={EventBG} classes={"object-cover"} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-decoration bg-decoration-1"></div>
      <div className="bg-decoration bg-decoration-2"></div>
      <div className="bg-decoration bg-decoration-3"></div>
    </div>
  );
};

export default Login;
